import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'
import { api } from '../lib/api'

const BASE_URL = import.meta.env.VITE_API_BASE || ''

// One key per document, holding the revision this browser last acknowledged.
const storageKey = (type: string) => `lupa.legalSeen.${type}`

// The personal-data consent is deliberately absent: a consent under ст. 9 ч. 4 is not something
// we can update by notice. Either the change is editorial and the old acceptance still covers it,
// or it is substantive and ConsentGate asks again. A banner would blur that line.
const WATCHED = ['terms', 'privacy'] as const

type WatchedType = (typeof WATCHED)[number]

const TITLES: Record<WatchedType, string> = {
  terms: 'Пользовательское соглашение',
  privacy: 'Политика обработки персональных данных',
}

type LegalDocMeta = { type: string; version: string; updated_at?: string }

// When this person last accepted the document set, or null if unknown. Asked only for a browser
// that has never recorded a revision — see the component comment.
async function acceptedOn(): Promise<string | null> {
  try {
    return (await api.consentStatus()).terms_accepted_on ?? null
  } catch {
    return null
  }
}

function readSeen(type: string): string | null {
  try {
    return localStorage.getItem(storageKey(type))
  } catch {
    return null
  }
}

function writeSeen(type: string, version: string) {
  try {
    localStorage.setItem(storageKey(type), version)
  } catch {
    // Private mode without storage — the notice reappears next time, which is the safe side.
  }
}

/**
 * Non-blocking notice that a legal document has a new revision.
 *
 * п. 8.1 of the user agreement promises that material changes are "доведены до Пользователей
 * уведомлением в Сервисе или по электронной почте", and the same clause makes continued use the
 * acceptance — so informing is the whole obligation here, and nothing has to be blocked. That is
 * also the ordinary pattern outside B2B: a bar the person dismisses, not a wall.
 *
 * A browser that has never recorded a revision — every browser, the first time this component
 * runs, and any new device later — falls back to what the server knows: the date this person last
 * accepted the set. A revision dated after it is announced; anything else is seeded silently, so a
 * fresh sign-up is not told about an "update" it cannot have missed. Without that fallback the
 * very first revision shipped together with this component would have reached nobody. Shown to
 * signed-in users only: the promise is to Пользователи, not to every visitor.
 */
export default function LegalUpdateNotice() {
  const { authenticated } = useAuth()
  // type → the revision being announced, i.e. what gets recorded as seen on acknowledge.
  const [updated, setUpdated] = useState<Partial<Record<WatchedType, string>>>({})
  const types = Object.keys(updated) as WatchedType[]

  useEffect(() => {
    if (!authenticated) {
      setUpdated({})
      return
    }

    let cancelled = false
    fetch(`${BASE_URL}/api/legal/documents`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('legal list failed'))))
      // The list comes wrapped — {"documents": [...]} (backend legalController.list). Reading it
      // as a bare array made this component silently render nothing for everyone.
      .then(async (body: { documents?: LegalDocMeta[] }) => {
        const docs = body?.documents
        if (cancelled || !Array.isArray(docs)) return
        const watched = docs.filter((doc) => WATCHED.includes(doc.type as WatchedType) && doc.version)
        const since = watched.some((doc) => readSeen(doc.type) === null) ? await acceptedOn() : null
        if (cancelled) return

        const changed: Partial<Record<WatchedType, string>> = {}
        for (const doc of watched) {
          const seen = readSeen(doc.type)
          if (seen === null) {
            // First sight in this browser: announce only what is newer than the person's own
            // acceptance. Dates are YYYY-MM-DD on both sides, so string order is date order.
            if (since && doc.updated_at && doc.updated_at > since) {
              changed[doc.type as WatchedType] = doc.version
            } else {
              writeSeen(doc.type, doc.version)
            }
          } else if (seen !== doc.version) {
            changed[doc.type as WatchedType] = doc.version
          }
        }
        setUpdated(changed)
      })
      // A failed lookup must stay silent: this is an informational bar, not enforcement.
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [authenticated])

  if (types.length === 0) return null

  // Record the revision the bar actually announced, not whatever is current now: a revision
  // published while the bar was on screen has not been shown to anyone yet.
  const acknowledge = () => {
    for (const type of types) writeSeen(type, updated[type] as string)
    setUpdated({})
  }

  return (
    <>
      <style>{`
        .legal-update {
          position: fixed; left: 16px; right: 16px; bottom: 16px; z-index: 1000;
          margin: 0 auto; max-width: 640px;
          background: rgba(15, 23, 42, 0.96); color: #e2e8f0;
          border: 1px solid rgba(148, 163, 184, 0.25);
          border-radius: 16px; padding: 14px 18px;
          display: flex; align-items: center; gap: 14px;
          box-shadow: 0 12px 40px rgba(0,0,0,0.35);
          font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, sans-serif;
        }
        .legal-update__text { flex: 1; font-size: 13px; line-height: 1.5; }
        .legal-update__text a { color: #86efac; text-decoration: underline; text-underline-offset: 2px; }
        .legal-update__btn {
          flex-shrink: 0; padding: 9px 18px; border: none; border-radius: 10px;
          background: #15803d; color: #fff; font-size: 13px; font-weight: 600;
          cursor: pointer; font-family: inherit;
        }
        .legal-update__btn:hover { background: #166534; }
        @media (max-width: 520px) {
          .legal-update { flex-direction: column; align-items: stretch; text-align: left; }
          .legal-update__btn { width: 100%; }
        }
      `}</style>
      <div className="legal-update" role="region" aria-label="Обновление правовых документов">
        <div className="legal-update__text">
          {types.length === 1 ? 'Обновился документ: ' : 'Обновились документы: '}
          {types.map((type, i) => (
            <span key={type}>
              {i > 0 && ', '}
              <Link to={`/legal/${type}`}>{TITLES[type]}</Link>
            </span>
          ))}
          . Продолжая пользоваться сервисом, вы принимаете новую редакцию.
        </div>
        <button type="button" className="legal-update__btn" onClick={acknowledge}>
          Понятно
        </button>
      </div>
    </>
  )
}
